import React from "react";
import { motion } from "framer-motion";
import { Heart, TrendingUp, UserCheck, CheckSquare, Award, Ban } from "lucide-react";

const reasons = [
  {
    icon: Heart,
    title: "Counselling-first",
    body: "Clarity before paperwork. We understand the student and family situation before suggesting any direction.",
    iconColor: "#2B2F9E",
    bg: "#EEF0FC",
  },
  {
    icon: TrendingUp,
    title: "ROI and scholarship focus",
    body: "Scholarship and funding options are assessed in every recommendation. Outcomes and return on investment guide every shortlist, not rankings alone.",
    iconColor: "#F5A000",
    bg: "#FFF7E6",
  },
  {
    icon: UserCheck,
    title: "Founder-led guidance",
    body: "Expert input from Anirudh Gupta, not handed off to juniors. Every family gets the same quality of guidance.",
    iconColor: "#10B981",
    bg: "#F0FDF4",
  },
  {
    icon: CheckSquare,
    title: "Strategy plus execution",
    body: "Checklists, review cycles, timelines. Every step is tracked and quality-controlled. Flawless. On time.",
    iconColor: "#9333EA",
    bg: "#FDF4FF",
  },
  {
    icon: Award,
    title: "A partner to your family's decisions.",
    body: "No commissions. No hidden incentives. No conflicts. Fee-based, independent advisory that works solely for your outcomes.",
    iconColor: "#CA8A04",
    bg: "#FEFCE8",
  },
  {
    icon: Ban,
    title: "Honest guidance, including a clear no",
    body: "If studying abroad is not the right fit, we will tell you. We are comfortable telling you not to go abroad when the fit is wrong. Clear advice matters more than a yes.",
    iconColor: "#EF4444",
    bg: "#FFF1F2",
  },
];

export default function WhyMindways() {
  return (
    <section
      id="why-mindways"
      data-testid="why-mindways-section"
      style={{
        padding: "100px 0",
        backgroundColor: "#2B2F9E",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div style={{ position: "absolute", top: "-100px", right: "-100px", width: "400px", height: "400px", borderRadius: "50%", backgroundColor: "rgba(245,160,0,0.06)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", bottom: "-100px", left: "-60px", width: "300px", height: "300px", borderRadius: "50%", backgroundColor: "rgba(255,255,255,0.04)", pointerEvents: "none" }} />

      <div style={{ maxWidth: "1280px", margin: "0 auto", padding: "0 24px", position: "relative", zIndex: 1 }}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          style={{ textAlign: "center", marginBottom: "60px" }}
        >
          <span style={{ fontSize: "13px", fontWeight: "700", letterSpacing: "0.12em", textTransform: "uppercase", color: "#F5A000", fontFamily: "'DM Sans', sans-serif" }}>
            Why Families Choose Mindways
          </span>
          <h2 style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: "clamp(26px, 2.5vw, 36px)",
            fontWeight: "700",
            color: "#fff",
            margin: "16px 0 16px",
            lineHeight: "1.25",
          }}>
            Not just advisors. Part of your team.
          </h2>
          <p style={{ fontSize: "17px", color: "rgba(255,255,255,0.75)", fontFamily: "'DM Sans', sans-serif", lineHeight: "1.65", maxWidth: "560px", margin: "0 auto" }}>
            Mindways has been guiding overseas journeys since 2005. A track record built one student at a time.
          </p>
        </motion.div>

        {/* Reasons grid */}
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          variants={{ show: { transition: { staggerChildren: 0.09 } } }}
          className="mw-grid-3"
        >
          {reasons.map((r, i) => {
            const Icon = r.icon;
            return (
              <motion.div
                key={r.title}
                variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0 } }}
                data-testid={`why-reason-${i}`}
                style={{
                  display: "flex",
                  gap: "16px",
                  alignItems: "flex-start",
                  backgroundColor: "rgba(255,255,255,0.07)",
                  borderRadius: "12px",
                  padding: "24px",
                  border: "1px solid rgba(255,255,255,0.1)",
                  transition: "background-color 0.2s",
                }}
                whileHover={{ backgroundColor: "rgba(255,255,255,0.12)" }}
              >
                <div style={{
                  width: "44px", height: "44px", borderRadius: "10px",
                  backgroundColor: r.bg,
                  display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                }}>
                  <Icon size={20} style={{ color: r.iconColor }} />
                </div>
                <div>
                  <h4 style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "16px", fontWeight: "700", color: "#fff", margin: "0 0 6px" }}>
                    {r.title}
                  </h4>
                  <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "14px", color: "rgba(255,255,255,0.75)", margin: 0, lineHeight: "1.65" }}>
                    {r.body}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Stats row */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="mw-grid-stats"
        >
          {[
            ["20+", "Years counselling families"],
            ["5,000+", "Careers built"],
            ["99%", "Visa success rate"],
            ["1-on-1", "Personalised, always"],
          ].map(([val, label]) => (
            <div key={val} style={{ textAlign: "center", padding: "0 16px", borderRight: "1px solid rgba(255,255,255,0.12)" }}>
              <div style={{ fontSize: "36px", fontWeight: "700", color: "#F5A000", fontFamily: "'Playfair Display', serif", lineHeight: "1.1" }}>{val}</div>
              <div style={{ fontSize: "13px", color: "rgba(255,255,255,0.65)", fontFamily: "'DM Sans', sans-serif", marginTop: "6px" }}>{label}</div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
